import React, { useEffect } from "react";
import Icon from "components/Elements/Icon/Icon.tsx";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import parse from "html-react-parser";
import { Toggle } from "components/Elements/Toggle/Toggle.js";
import { useToasts } from "react-toast-notifications";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { useUser } from "contexts/userContext/userContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import EmbedLimitOptionsModal from "components/Reusable/JUMBOform/Modals/EmbedLimitOptionsModal.js";
import { spacesRegex, numbersAndLettersRegex } from "helpers/validators.js";
import useBucketName from "hooks/useBucketName";

const SeoOption = ({
  headScripts,
  setHeadScripts,
  bodyScripts,
  setBodyScripts,
  setMetaDescription,
  metaDescription,
  setBrowserTitle,
  browserTitle,
  scriptsDetails,
  setInvalidUserName,
  invalidUserName,
  setTempUserName,
  tempUserName,
  setExistUserName,
  existUserName,
  searchEngines,
  setSearchEngines,
  FullDetailsEmbedPage,
}) => {
  const { isPremium, isBusiness } = useUser();
  const { addToast } = useToasts();
  const { setModal } = useModal();
  const maxLimitBrowser = 70;
  const maxLimitMetaDescription = 160;
  const statusScripts = scriptsDetails
    ? scriptsDetails.EmbedPageScriptsStatus
    : null;
  const { getTextByKey, getDirection } = useContent();
  const headTooltip = parse(getTextByKey("head-scripts-tooltip"));

  const bucketName = useBucketName();

  const handleChangeBrowserTitle = (e) => {
    if (e.length <= maxLimitBrowser) {
      setBrowserTitle(e);
    }
  };

  const handleChangeMetaDescription = (e) => {
    if (e.length <= maxLimitMetaDescription) {
      setMetaDescription(e);
    }
  };

  const checkInvalidUserName = (userName) => {
    setTempUserName(userName.trim());
    setExistUserName(false);
    if (userName === "") {
      setInvalidUserName(true);
    }
    const validURL = new RegExp(numbersAndLettersRegex);
    if (validURL.test(userName) && spacesRegex.test(userName)) {
      setInvalidUserName(false);
    } else {
      setInvalidUserName(true);
    }
  };

  const returnExistUserNameText = (txt) => {
    let tempTxt = getTextByKey(txt);
    if (tempTxt.includes("#UserName#")) {
      tempTxt = tempTxt.replace("#UserName#", tempUserName);
    }

    return tempTxt;
  };

  const updateSearchEngines = () => {
    if (isPremium() || isBusiness()) {
      const newValSeachEngines = !searchEngines;
      setSearchEngines(newValSeachEngines);
      updateEnginesApi(newValSeachEngines);
    } else {
      setModal(() => (
        <EmbedLimitOptionsModal typeOfModal={"business-premium"} />
      ));
    }
  };

  const updateEnginesApi = async (enginges) => {
    let body = {
      EmbedDynamicPageId: FullDetailsEmbedPage.EmbedDynamicPage.EmbedPageId,
      EmbedDynamicPageUsername:
        FullDetailsEmbedPage.EmbedDynamicPage.EmbedPageUserName,
      EmbedDynamicPageActive:
        FullDetailsEmbedPage.EmbedDynamicPage.EmbedPageActive,
      EmbedDynamicSearchEngines: enginges,
    };

    const response = await apiRequest(
      "EmbedDynamics",
      "UpdateEmbedPageSearchEngines",
      body,
      "POST"
    );
    if (response.status === "success") {
      if (response.data) {
        if (response.data.EmbedDybamicPageResponseCode === 0) {
          addToast(getTextByKey("update-search-engines-success"), {
            appearance: "success",
          });
        }
      }
    }
  };

  const returnEnginesText = (text) => {
    const tempText = getTextByKey(text);
    return tempText;
  };

  const pageUrl = `https://${bucketName}/page/${tempUserName.toLowerCase()}`;

  return (
    <div className="seo-option">
      <div className="seo-option-title">
        <span>{getTextByKey("personal-seo-tooltip")}</span>
      </div>
      <div className="page-url part">
        <div className="title">
          <span className="title-span">
            {getTextByKey("settings-embed-page-your-url")}
          </span>
          <Tooltip
            id="embed-page-url-tooltip"
            tooltipTextKey="embed-page-url-tooltip"
            end={getDirection() === "rtl" ? false : true}
            //hardPositioned={getDirection() === "rtl" ? true : false}
          >
            <div className={`tootltip-icon-container`}>
              <Icon name="question-circle" iconClass="tooltip-question" />
            </div>
          </Tooltip>
        </div>
        <div
          className={`div-input url-input ${invalidUserName} ${existUserName}`}
        >
          <input
            value={tempUserName}
            onChange={(e) => checkInvalidUserName(e.target.value)}
          />
        </div>
        <span className="page-url-example">
          {bucketName ? (
            <a href={pageUrl} target="_blank" rel="noopener noreferrer">
              {pageUrl}
            </a>
          ) : null}
        </span>
        {existUserName && (
          <span className="exist-embed-url-text">
            {returnExistUserNameText("exist-embed-page-url")}
          </span>
        )}
      </div>
      <div
        className={`browser-title part ${
          browserTitle.length >= maxLimitBrowser ? "max-limit" : ""
        }`}
      >
        <div className="title">
          <span className="title-span">
            {getTextByKey("seo-settings-browser-title")}
          </span>
          <Tooltip
            id="browser-title-tooltip-id"
            tooltipTextKey="browser-title-tooltip"
            bottom
          >
            <div className={`tootltip-icon-container`}>
              <Icon name="question-circle" iconClass="tooltip-question" />
            </div>
          </Tooltip>
        </div>
        <div className="div-input">
          <input
            type="text"
            value={browserTitle}
            onChange={(e) => handleChangeBrowserTitle(e.target.value)}
          />
        </div>
        <div className="max-chars">
          <span className="show-chars" style={{ direction: "ltr" }}>
            {browserTitle.length} / {maxLimitBrowser}
          </span>
          <span>{getTextByKey("seo-settings-limit-characters")}</span>
        </div>
      </div>
      <div
        className={`meta-description part ${
          metaDescription.length >= maxLimitMetaDescription ? "max-limit" : ""
        }`}
      >
        <div className="title">
          <span className="title-span">
            {getTextByKey("seo-settings-meta-description")}
          </span>
          <Tooltip
            clickable
            id="meta-description"
            tooltipTextKey="meta-description-tooltip"
            end={getDirection() === "rtl" ? false : true}
            //hardPositioned={getDirection() === "rtl" ? true : false}
          >
            <div className={`tootltip-icon-container`}>
              <Icon name="question-circle" iconClass="tooltip-question" />
            </div>
          </Tooltip>
        </div>
        <div className="div-input textarea-div">
          <textarea
            value={metaDescription}
            onChange={(e) => handleChangeMetaDescription(e.target.value)}
          />
        </div>
        <div className="max-chars">
          <span className="show-chars" style={{ direction: "ltr" }}>
            {metaDescription.length} / {maxLimitMetaDescription}
          </span>
          <span>{getTextByKey("meta-description-limit-chars")}</span>
        </div>
      </div>
      <div className="page-scripts part">
        <div className="page-scripts-title">
          <span>{getTextByKey("seo-settings-page-scripts-title")}</span>
          {((statusScripts && scriptsDetails.EmbedPageScriptHead) ||
            (statusScripts && scriptsDetails.EmbedPageScriptBody)) && (
            <span>{` (${getTextByKey("status")}: ${statusScripts})`}</span>
          )}
        </div>
        <div className="text-info">
          <span>{getTextByKey("seo-settings-page-scripts-sub-title")}</span>
        </div>
      </div>
      <div className="part">
        <div className="title">
          {" "}
          <span className="title-span">Head</span>
          <Tooltip
            id="head-scripts"
            tooltipText={headTooltip}
            end
            // hardPositioned
          >
            <div className={`tootltip-icon-container`}>
              <Icon name="question-circle" iconClass="tooltip-question" />
            </div>
          </Tooltip>
        </div>
        <div className="div-input textarea-div">
          <textarea
            disabled={isPremium() || isBusiness() ? false : true}
            value={headScripts}
            onChange={(e) => setHeadScripts(e.target.value)}
          />
        </div>
      </div>
      <div className="part">
        <div className="title">
          <span className="title-span">Body</span>
          <Tooltip
            id="body-scripts"
            tooltipTextKey="body-scripts-tooltip"
            end
            // hardPositioned
          >
            <div className={`tootltip-icon-container`}>
              <Icon name="question-circle" iconClass="tooltip-question" />
            </div>
          </Tooltip>
        </div>
        <div className="div-input textarea-div">
          <textarea
            disabled={isPremium() || isBusiness() ? false : true}
            value={bodyScripts}
            onChange={(e) => setBodyScripts(e.target.value)}
          />
        </div>
      </div>
      <div className="part engines-part">
        <div className="toggle-div">
          <Toggle
            setStatus={updateSearchEngines}
            status={isPremium() || isBusiness() ? searchEngines : false}
          />
        </div>
        <div className="toggle-text">
          <span className="title-span">
            {getTextByKey("search-engines-title")}
          </span>
          <div className="engines-text">
            <span>{returnEnginesText("search-engines-text")}</span>{" "}
            <LinkToNewTab isNewTab page={"support"}>
              {getTextByKey("search-engines-text-url")}
            </LinkToNewTab>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeoOption;
